import { useState, useEffect, useMemo, useCallback } from 'react'
import { supabase } from '../lib/supabase'

// Cast to any to bypass strict typing - tables exist in DB but not in generated types
const db = supabase as any

// Character limit for friend notes
const MAX_NOTE_LENGTH = 1000

// Debounce delay in milliseconds (1 second after user stops typing)
const DEBOUNCE_DELAY = 1000

export interface FriendNote {
  id: string
  user_id: string
  friend_id: string
  note: string
  created_at: string
  updated_at: string
}

export interface UseFriendNoteReturn {
  note: string
  loading: boolean
  saving: boolean
  error: string | null
  characterCount: number
  isAtLimit: boolean
  updateNote: (noteText: string) => void
  deleteNote: () => Promise<void>
  saveNow: () => Promise<void>
}

/**
 * Hook to manage friend notes with auto-save, debouncing, and real-time sync
 *
 * @param userId - The current user's ID (note author)
 * @param friendId - The friend's ID (note subject)
 * @returns Object with note state and management functions
 */
export function useFriendNote(userId: string | null, friendId: string | null): UseFriendNoteReturn {
  const [note, setNote] = useState<string>('')
  const [originalNote, setOriginalNote] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(true)
  const [saving, setSaving] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null)

  // Character count and limit check
  const characterCount = note.length
  const isAtLimit = characterCount >= MAX_NOTE_LENGTH

  // Load note from database
  const loadNote = useCallback(async () => {
    if (!userId || !friendId) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const { data, error: fetchError } = await db
        .from('friend_notes')
        .select('note, updated_at')
        .eq('user_id', userId)
        .eq('friend_id', friendId)
        .maybeSingle()

      if (fetchError) throw fetchError

      const noteText = data?.note || ''
      setNote(noteText)
      setOriginalNote(noteText)
    } catch (err) {
      console.error('Error loading friend note:', err)
      setError('Failed to load note. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [userId, friendId])

  // Save note to database
  const saveNote = useCallback(async (noteText: string) => {
    if (!userId || !friendId) return

    // Don't save if note hasn't changed
    if (noteText === originalNote) return

    try {
      setSaving(true)
      setError(null)

      // If note is empty, delete the row instead of saving
      if (noteText.trim() === '') {
        const { error: deleteError } = await db
          .from('friend_notes')
          .delete()
          .eq('user_id', userId)
          .eq('friend_id', friendId)

        if (deleteError) throw deleteError

        setOriginalNote('')
      } else {
        // Upsert note (create or update)
        const { error: upsertError } = await db
          .from('friend_notes')
          .upsert({
            user_id: userId,
            friend_id: friendId,
            note: noteText,
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'user_id,friend_id'
          })

        if (upsertError) throw upsertError

        setOriginalNote(noteText)
      }
    } catch (err) {
      console.error('Error saving friend note:', err)
      setError('Failed to save note. Please try again.')
    } finally {
      setSaving(false)
    }
  }, [userId, friendId, originalNote])

  // Debounced save function
  const debouncedSave = useMemo(
    () => (noteText: string) => {
      // Clear existing timer
      if (debounceTimer) {
        clearTimeout(debounceTimer)
      }

      // Set new timer
      const timer = setTimeout(() => {
        saveNote(noteText)
      }, DEBOUNCE_DELAY)

      setDebounceTimer(timer)
    },
    [debounceTimer, saveNote]
  )

  // Update note (called by component on text change)
  const updateNote = useCallback((noteText: string) => {
    // Enforce character limit
    if (noteText.length > MAX_NOTE_LENGTH) {
      noteText = noteText.substring(0, MAX_NOTE_LENGTH)
    }

    setNote(noteText)
    debouncedSave(noteText)
  }, [debouncedSave])

  // Force save immediately (useful for manual save or on unmount)
  const saveNow = useCallback(async () => {
    // Clear debounce timer
    if (debounceTimer) {
      clearTimeout(debounceTimer)
      setDebounceTimer(null)
    }

    await saveNote(note)
  }, [debounceTimer, note, saveNote])

  // Delete note
  const deleteNote = useCallback(async () => {
    if (!userId || !friendId) return

    try {
      setSaving(true)
      setError(null)

      const { error: deleteError } = await db
        .from('friend_notes')
        .delete()
        .eq('user_id', userId)
        .eq('friend_id', friendId)

      if (deleteError) throw deleteError

      setNote('')
      setOriginalNote('')
    } catch (err) {
      console.error('Error deleting friend note:', err)
      setError('Failed to delete note. Please try again.')
    } finally {
      setSaving(false)
    }
  }, [userId, friendId])

  // Load note on mount or when userId/friendId changes
  useEffect(() => {
    loadNote()
  }, [loadNote])

  // Real-time subscription for note updates
  useEffect(() => {
    if (!userId || !friendId) return

    const channel = supabase
      .channel(`friend_notes:${userId}:${friendId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'friend_notes',
          filter: `user_id=eq.${userId}`
        },
        (payload: any) => {
          // Only update if it's for the current friend
          if (payload.new && payload.new.friend_id === friendId) {
            const newNote = payload.new.note || ''
            setNote(newNote)
            setOriginalNote(newNote)
          }
          // Handle delete
          if (payload.eventType === 'DELETE' && payload.old?.friend_id === friendId) {
            setNote('')
            setOriginalNote('')
          }
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [userId, friendId])

  // Save note when component unmounts (if there are unsaved changes)
  useEffect(() => {
    return () => {
      // Clear debounce timer on unmount
      if (debounceTimer) {
        clearTimeout(debounceTimer)
      }

      // Save if there are unsaved changes
      if (note !== originalNote && userId && friendId) {
        // Can't use async in cleanup, so we fire and forget
        saveNote(note)
      }
    }
  }, []) // Empty deps to only run on unmount

  return {
    note,
    loading,
    saving,
    error,
    characterCount,
    isAtLimit,
    updateNote,
    deleteNote,
    saveNow
  }
}
